/**
 * Structural shape `filterDropdownOptions` needs from an option — kept local (rather than importing
 * `DropdownSelectOption`/`GroupedDropdownSelectOption` from `DropdownSelect.tsx`) so the helper stays
 * dependency-free and doesn't create an import cycle with the component that uses it. A grouped option is
 * one carrying its own `options` array.
 */
export type FilterableOption = { label?: string; sublabel?: string }

/**
 * Lowercases, then collapses every run of non-alphanumeric characters to a single space, and trims.
 *
 * This is what makes "Sherwin Williams" find a product whose manufacturer is stored as
 * "Sherwin-Williams" (ENG-967) — people do not type the hyphen. It also absorbs a trailing space from
 * the search box, and makes "S-W" match a label that starts "S-W ". It is not aliasing: "S-W" still
 * finds nothing in "Sherwin-Williams", because "s w" is not a substring of "sherwin williams".
 */
function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function matchesSearch(option: FilterableOption, search: string): boolean {
  // `label` is what's displayed, but the muted `sublabel` carries the part people search by often enough
  // to matter — a product's manufacturer ("Sherwin-Williams"), a user's email — so match on both.
  return [option?.label, option?.sublabel].some((text) => !!text && normalizeForSearch(text).includes(search))
}

/**
 * Narrows a DropdownSelect's options to those matching the search bar's text, on either `label` or
 * `sublabel`. Both sides are normalized first (see `normalizeForSearch`), so punctuation and spacing in
 * the query do not have to match the stored text.
 *
 * Grouped options keep the component's long-standing behavior: a group survives when any one of its
 * children matches, and all of that group's children still render.
 */
export function filterDropdownOptions<T extends FilterableOption>(args: {
  options?: T[]
  searchFilter?: string
  minimumSearchCharacters?: number
}): T[] {
  const { options, minimumSearchCharacters } = args
  const searchFilter = args.searchFilter ?? ''

  if (minimumSearchCharacters && searchFilter.length < minimumSearchCharacters) {
    return []
  }

  const search = normalizeForSearch(searchFilter)

  return (options ?? []).filter((option) => {
    // T is constrained to the fields we read, but a grouped option's children aren't expressible in that
    // constraint without making it self-referential (which then rejects the flat DropdownSelectOption
    // case), so the nested shape is asserted here.
    const candidate = option as T & { options?: FilterableOption[] }

    if (candidate?.options !== undefined) {
      return candidate.options.some((subOption) => matchesSearch(subOption, search))
    }

    return matchesSearch(candidate, search)
  })
}
